export type SyncStage =
  | 'Pending'
  | 'Connecting'
  | 'Discovering'
  | 'Fetching'
  | 'Mapping'
  | 'Staging'
  | 'Analyzing'
  | 'Resolving Managers'
  | 'Committing'
  // Deploy-proof sync: the worker parked the job (deploy/restart); it
  // resumes automatically. NOT terminal — keep polling.
  | 'Resuming'
  | 'Completed'
  | 'Completed With Errors'
  | 'Failed';

export type LegacySyncStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'no_domain'
  | 'skipped';

export interface SyncStats {
  totalEstimate: number | null;
  fetched: number;
  staged: number;
  analyzed: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
}

export interface SyncBatchMarker {
  index: number;
  size: number;
  total: number;
}

export interface SyncErrorDetail {
  code: string;
  message: string;
  retryCount: number;
  context?: Record<string, unknown>;
}

export interface SyncProgress {
  stage: SyncStage;
  percentage: number;
  message: string;
  stats: SyncStats;
  currentBatch: SyncBatchMarker | null;
  eta: number | null;
  startedAt: string;
  updatedAt: string;
  errors: SyncErrorDetail[];
  // Legacy (kept for BC with current SSE wire payload)
  status: LegacySyncStatus | string;
  totalUsers: number;
  syncedUsers: number;
  totalGroups: number;
  syncedGroups: number;
  canRetry?: boolean;
}

export const TERMINAL_STAGES: SyncStage[] = [
  'Completed',
  'Completed With Errors',
  'Failed',
];

export const SUCCESS_STAGES: SyncStage[] = [
  'Completed',
  'Completed With Errors',
];

export const STAGE_LABELS: Record<SyncStage, string> = {
  Pending: 'Waiting to start',
  Connecting: 'Connecting',
  Discovering: 'Discovering users',
  Fetching: 'Fetching users',
  Mapping: 'Applying field mappings',
  Staging: 'Staging users',
  Analyzing: 'Detecting duplicates',
  'Resolving Managers': 'Resolving groups',
  Committing: 'Finalizing sync',
  Resuming: 'Resuming after restart',
  Completed: 'Done',
  'Completed With Errors': 'Done (with warnings)',
  Failed: 'Failed',
};

/**
 * Visible stages in the timeline — collapses a few granular backend
 * stages into grouped steps the user cares about.
 */
export interface TimelineStep {
  id: string;
  label: string;
  covers: SyncStage[];
}

export const TIMELINE_STEPS: TimelineStep[] = [
  { id: 'connecting', label: 'Connecting to provider', covers: ['Connecting'] },
  {
    id: 'discovering',
    label: 'Discovering users and groups',
    covers: ['Discovering'],
  },
  { id: 'fetching', label: 'Fetching users', covers: ['Fetching', 'Mapping'] },
  {
    id: 'processing',
    label: 'Processing and deduplicating',
    covers: ['Staging', 'Analyzing', 'Resolving Managers'],
  },
  {
    id: 'finalizing',
    label: 'Saving to your workspace',
    covers: ['Committing'],
  },
];

export function timelineStepState(
  step: TimelineStep,
  current: SyncStage,
): 'pending' | 'running' | 'done' {
  // Terminal states — the timeline is typically hidden by the caller on
  // Failed, but if rendered we dim everything. Completed marks all done.
  if (current === 'Failed' || current === 'Pending') return 'pending';
  if (current === 'Completed' || current === 'Completed With Errors') {
    return 'done';
  }
  if ((step.covers as SyncStage[]).includes(current)) return 'running';
  const currentIdx = TIMELINE_STEPS.findIndex((s) =>
    (s.covers as SyncStage[]).includes(current),
  );
  const stepIdx = TIMELINE_STEPS.findIndex((s) => s.id === step.id);
  return stepIdx < currentIdx ? 'done' : 'pending';
}

export function formatEta(seconds: number | null): string | null {
  if (seconds === null || seconds <= 0) return null;
  if (seconds < 60) return `${seconds}s remaining`;
  const minutes = Math.round(seconds / 60);
  if (minutes === 1) return 'About 1 minute remaining';
  if (minutes < 60) return `About ${minutes} minutes remaining`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? 'About 1 hour remaining' : `About ${hours} hours remaining`;
}

export function isTerminal(stage: SyncStage): boolean {
  return TERMINAL_STAGES.includes(stage);
}
